(ns app.front.ui.cards
  (:require ["@carbon/icons-react/es/arrow--right/16" :default ArrowRight16]
            ["@carbon/icons-react/es/chevron--down/16" :default ChevronDown16]
            ["@carbon/icons-react/es/chevron--up/16" :default ChevronUp16]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn card [{:keys               [href title subtitle light?]
             {:keys [kind text]} :pill}]
  [:div.flex.w-full.md:w-1-2.lg:w-1-3.px-2.mt-3
   [:a.flex.flex-col.w-full.rounded.items-center.justify-between.px-5.py-3.text-black.no-underline
    {:class (cond light? "bg-ui-white"
                  (not kind) "bg-ui-blue-10"
                  :else "bg-ui-light-gray")
     :href  href}
    [:h4.w-full.mb-4.text-sm.uppercase title]
    [:div.flex.items-center.justify-between.w-full
     [:span.text-xs subtitle]
     (when (and kind text)
       [ui/small-pill (util/mmap kind) text])]]])

(def cards-container
  :div.flex.flex-wrap.-mx-2.-mt-3)

(def landing-hr :hr.border-1.border-gray-200.m-0.p-0)
(def landing-card-width "304px")

(defn- landing-card
  "Create one card in the `landing-cards` component."
  [{:keys [active-card type key title description content img href on-click action-text description-lines open-in-new-tab?]}]
  (r/with-let [focused          (r/atom nil)
               toggle-expansion (fn [active-key]
                                  (when (not= key active-key)
                                    key))
               expanded?        (fn [] (= key @active-card))
               link?            (= type :link)
               button?          (= type :button)]

    ;; Tab manages focus, Enter to click/expand/navigate
    [(cond
       button? :button.block.p-0.text-left
       link?   :a.block
       :else   :div)
     (merge
       {:style {:width landing-card-width}
        :class (str "grow flex-col rounded border-solid overflow-hidden shadow appearance-none no-underline text-ui-black border-2"
                    (if (expanded?)
                      " bg-ui-blue-20 border-ui-blue-60"
                      " bg-white border-gray-400"))}
       (when link?
         (cond-> {:href href}
           open-in-new-tab? (assoc :rel "noopener" :target "_blank")))
       (when button?
         (util/mmap on-click)))

     ;; Header area — handles expand/collapse click for non-link/button cards
     [:div.flex.p-2.justify-between.h-16.md:h-32
      (merge
        {:class "cursor-pointer hover:bg-ui-blue-20 rounded-t"}
        (when-not (or link? button?)
          {:tabIndex    0
           :role        "button"
           :on-focus    (fn [_] (reset! focused true))
           :on-blur     (fn [_] (reset! focused false))
           :on-key-down (fn [e]
                          (when (and @focused (= "Enter" (.-code e)))
                            (swap! active-card toggle-expansion)))
           :on-click    (fn [_]
                          (swap! active-card toggle-expansion))}))
      (when img [:img.w-10.h-10.md:w-20.md:h-20.my-auto.ml-2.mr-4 {:src img :alt title}])
      [:div.grow.flex.justify-between
       [:div.grow.m-0
        [:p.text-md.tracking-tight.antialiased.p-0.sm:mt-3.mx-auto title]
        (when (and description (not (expanded?)))
          [:p.text-xs.p-0.mx-auto.my-2.overflow-hidden.wrap-anywhere
           {:style {:display            "-webkit-box"
                    :-webkit-line-clamp (or description-lines 3)
                    :-webkit-box-orient "vertical"}}
           description])]
       [:div.m-1
        [:> (cond
              (or link? button?) ArrowRight16
              (expanded?)        ChevronUp16
              :else              ChevronDown16)]]]]

     ;; when the active-card is the key of a card, the card is expanded
     (when (expanded?)
       [:div.animation-appear-slide-down
        (when-not (empty? content)
          [:div
           [landing-hr]
           [:div.text-xs.p-4.text-gray-800
            [ui-rt/editor {:initial-value      content
                           :disable-read-more? true}]]])
        (when (and (or href on-click) action-text)
          [:div
           [landing-hr]
           [:div.p-3.border-none.shadow-none.bg-transparent.flex
            (ui/button (cond-> {:size       :small
                                :type       :button
                                :class-name "ms-auto"}
                         (and href (not on-click)) (assoc :href href)
                         on-click                  (assoc :on-click on-click))
                       action-text)]])])]))

;; Display a grid of cards intended to be on public landing pages. `cards`
;; is a vector of data defining the cards to display. Each is a map with:
;; `:type`, `:title`, `:description`, `:content`, `:img`, `:href`, `:on-click` and
;; `:action-text`. The `:type` can be `:link`, `:button` or nil (for expandable cards as a div).
;; A `:link` or `:button` type has an arrow icon on the card, while expandable cards have a chevron.

(defn landing-cards
  [{:keys [cards description-lines]}]
  ;; active-card is the index of the currently expanded card, or nil for all closed
  (r/with-let [active-card (r/atom nil)]
    [:div.flex.flex-wrap.items-start.gap-2.py-2.md:gap-4.md:py-4
     {;; clicking the container background (gap between cards) closes all cards.
      ;; Only fires when the click target is the container itself, not a child,
      ;; so we don't need stopPropagation on cards (which would block reitit's
      ;; document-level click handler for client-side navigation).
      :on-click (fn [e]
                  (when (= (.-currentTarget e) (.-target e))
                    (reset! active-card nil)))}
     (futil/for-frag-indexed [[key card] cards]
       ;; :key is index of card, expand a card by setting active-card to key
       [landing-card (merge card (util/mmap key active-card description-lines))])]))
